import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RedirectView } from '@agency/contracts';
import { Badge, Button, EmptyState, Skeleton, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, del, get } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate, relative } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { ConfirmDrawer, errText } from '../settings/common.tsx';

export function RedirectsTab({ onCreate }: { onCreate: () => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const manage = can('seo.manage');
  const [filter, setFilter] = useState('');
  const [removing, setRemoving] = useState<RedirectView | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const q = useQuery<RedirectView[], ApiClientError>({ queryKey: ['seo', 'redirects'], queryFn: () => get('/admin/seo/redirects') });
  const remove = useMutation({
    mutationFn: (r: RedirectView) => del(`/admin/seo/redirects/${r.id}`),
    onSuccess: async (_v, r) => { await qc.invalidateQueries({ queryKey: ['seo'] }); toast({ tone: 'success', message: `Deleted the redirect from ${r.source}.` }); setRemoving(null); },
    onError: (e) => setRemoveError(errText(e)),
  });
  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (q.data ?? []).filter((r) => !f || r.source.toLowerCase().includes(f) || (r.destination ?? '').toLowerCase().includes(f));
  }, [q.data, filter]);

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__search"><TextField label="Filter redirects" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Source or destination" /></div>
        {manage ? <Button variant="primary" icon="plus" onClick={onCreate}>New redirect</Button> : null}
      </div>
      {q.isLoading ? <Skeleton width="100%" height={220} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? (
        <EmptyState title="No redirects" icon="external" action={manage ? <Button variant="primary" icon="plus" onClick={onCreate}>New redirect</Button> : undefined}>Redirects are created automatically when a published page’s address changes, or by you here. Loops and redirects that would hide a live page are rejected.</EmptyState>
      ) : rows.length === 0 ? <EmptyState title="No redirects match" icon="search">Nothing matches “{filter}”. Clear the filter to see all {q.data!.length}.</EmptyState> : (
        <div className="ag-table-wrap" {...scrollRegion('Redirects')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Redirects, newest first. {rows.length} shown.</caption>
            <thead><tr><th scope="col">Source</th><th scope="col">Destination</th><th scope="col">Response</th><th scope="col">Origin</th><th scope="col" className="ag-num">Hits</th><th scope="col">Last hit</th>{manage ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><span className="ag-cell-2"><span className="ag-mono">{r.source}</span>{r.reason ? <span>{r.reason}</span> : null}</span></td>
                  <td>{r.destination ? <span className="ag-mono">{r.destination}</span> : <span className="ag-muted">Removed (no destination)</span>}</td>
                  <td><Badge tone={r.statusCode === 410 ? 'warning' : r.statusCode === 302 ? 'info' : 'neutral'}>{r.statusCode}</Badge></td>
                  <td><Badge tone={r.auto ? 'accent' : 'neutral'}>{r.auto ? 'Automatic' : 'Manual'}</Badge></td>
                  <td className="ag-num">{r.hits}</td>
                  <td>{r.lastHitAt ? <time dateTime={r.lastHitAt} title={fmtDate(r.lastHitAt)}>{relative(r.lastHitAt)}</time> : <span className="ag-muted">Never</span>}</td>
                  {manage ? <td style={{ textAlign: 'right' }}><Button size="sm" variant="ghost" onClick={() => { setRemoveError(null); setRemoving(r); }} aria-label={`Delete redirect from ${r.source}`}>Delete…</Button></td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!manage ? <p className="ag-muted" style={{ marginTop: 12 }}>You can view redirects but not change them.</p> : null}
      <ConfirmDrawer open={!!removing} title="Delete redirect?" confirmLabel="Delete redirect" pending={remove.isPending} error={removeError} onClose={() => setRemoving(null)} onConfirm={() => removing && remove.mutate(removing)}>
        {removing ? (
          <p>Visitors and search engines requesting <span className="ag-mono">{removing.source}</span> will get a 404 instead of {removing.destination ? <>going to <span className="ag-mono">{removing.destination}</span></> : 'a 410 Gone'}.{removing.auto ? ' This redirect was created automatically when a page address changed.' : ''}</p>
        ) : null}
      </ConfirmDrawer>
    </>
  );
}
